import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Brand } from "../components/common";
import {
  Brain, ArrowRight, Zap, GitBranch, Sparkles, ShieldCheck, Search, Cpu, Database,
  MessageSquare, Building2, Check, Layers, Gauge, TrendingUp, ArrowUpRight,
} from "lucide-react";

function Counter({ to, suffix = "", prefix = "", duration = 1600 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start;
    const step = (t) => { if (!start) start = t; const p = Math.min((t - start) / duration, 1); setVal(Math.floor((1 - Math.pow(1 - p, 3)) * to)); if (p < 1) requestAnimationFrame(step); };
    requestAnimationFrame(step);
  }, [inView, to, duration]);
  return <span ref={ref}>{prefix}{val.toLocaleString()}{suffix}</span>;
}

const navyBtn = "rounded-full bg-[#0f2547] text-white hover:bg-[#17335c] border-0 pm-transition shadow-lg shadow-indigo-900/20";
const gradBtn = "rounded-full pm-gradient text-white border-0 pm-transition hover:opacity-90 shadow-lg shadow-sky-900/25";

function Nav() {
  const navigate = useNavigate();
  const { user } = useAuth();
  return (
    <header className="fixed top-4 inset-x-0 z-50 px-4">
      <div className="max-w-6xl mx-auto glass rounded-full px-4 h-16 flex items-center justify-between">
        <div className="pl-1"><Brand size="sm" /></div>
        <nav className="hidden md:flex items-center gap-1 text-sm font-medium text-slate-500">
          {[["Capabilities", "capabilities"], ["Why different", "gaps"], ["ROI", "roi"], ["Platform", "platform"]].map(([l, id]) => (
            <a key={id} href={`#${id}`} className="px-3 py-2 rounded-full hover:bg-white/70 hover:text-[#0f2547] pm-transition">{l}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate("/login")} data-testid="landing-signin" className="rounded-full text-[#0f2547] hover:bg-white/70">Sign in</Button>
          <Button onClick={() => navigate(user ? "/app" : "/login")} data-testid="landing-cta" className={`${navyBtn} h-10 px-5`}>{user ? "Open workspace" : "Book a demo"}<ArrowRight className="w-4 h-4 ml-1" /></Button>
        </div>
      </div>
    </header>
  );
}

function AgentCard() {
  const [progress, setProgress] = useState(0);
  useEffect(() => { const t = setInterval(() => setProgress((p) => (p >= 100 ? 100 : p + 4)), 45); return () => clearInterval(t); }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.15 }} className="relative">
      <div className="glass-strong rounded-[28px] p-6 pm-float">
        <div className="flex items-center gap-2 mb-3">
          <span className="w-2 h-2 rounded-full pm-gradient pm-pulse" />
          <span className="text-xs font-semibold tracking-wide text-[#0e7aa6] uppercase">AI Agent</span>
          <span className="text-sm text-slate-500 ml-1">Analysing your processes…</span>
        </div>
        <div className="h-2 rounded-full neu-inset overflow-hidden mb-5"><div className="h-full pm-gradient rounded-full pm-transition" style={{ width: `${progress}%` }} /></div>
        <div className="space-y-3">
          {[[GitBranch, "Discovered process", "9 activities · 6 variants", "#0e7aa6"],
            [TrendingUp, "Top bottleneck", "Invoice approval · 36h wait", "#c19a3e"],
            [Sparkles, "Opportunity found", "Automate 3-way matching", "#16a9c7"]].map(([Icon, t, d, c], i) => (
            <div key={i} className="neu rounded-2xl p-3.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${c}1a` }}><Icon className="w-4 h-4" style={{ color: c }} /></div>
              <div className="min-w-0"><div className="text-sm font-semibold text-[#0f2547] truncate">{t}</div><div className="text-xs text-slate-500 truncate">{d}</div></div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between neu rounded-2xl p-4">
          <div><div className="text-xs text-slate-400 uppercase tracking-wide">Est. recovery</div><div className="font-display text-3xl font-semibold text-[#0f2547]">$1.4M<span className="text-base text-slate-400">/yr</span></div></div>
          <Check className="w-6 h-6 text-emerald-500" />
        </div>
      </div>
      <div className="absolute -z-10 -right-8 top-10 w-48 h-48 rounded-full bg-[#0e7aa6]/25 blur-3xl" />
      <div className="absolute -z-10 -left-8 bottom-6 w-40 h-40 rounded-full bg-[#16a9c7]/25 blur-3xl" />
    </motion.div>
  );
}

function Hero() {
  const navigate = useNavigate();
  return (
    <section className="relative pt-40 pb-20 pm-os-bg overflow-hidden">
      <div className="relative max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-1.5 text-xs font-semibold text-[#0e7aa6] mb-6"><Sparkles className="w-3.5 h-3.5" /> AI-native process intelligence</div>
          <h1 className="font-display text-[3.2rem] lg:text-7xl font-semibold text-[#0f2547] leading-[0.98]">
            See how your<br />business <span className="italic text-gradient">truly</span> runs.
          </h1>
          <p className="text-lg text-slate-600 mt-6 max-w-lg leading-relaxed font-body">
            ProClytic ingests events from any system — ERP, CRM, ITSM, EMR, RPA, even emails — then applies large language models to auto-discover, explain and optimise every process. No ETL. No consultants. No waiting.
          </p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Button onClick={() => navigate("/login")} data-testid="hero-cta" className={`${navyBtn} h-12 px-7 text-base`}>Start exploring<ArrowRight className="w-4 h-4 ml-1.5" /></Button>
            <Button variant="outline" onClick={() => navigate("/login")} className="rounded-full h-12 px-7 text-base bg-white border-slate-200 text-[#0f2547] hover:bg-slate-50 shadow-sm">Watch it work</Button>
          </div>
        </motion.div>
        <AgentCard />
      </div>
      <div className="max-w-6xl mx-auto px-6 mt-16 grid grid-cols-3 gap-5">
        {[["70", "%", "faster time-to-insight"], ["25", "+", "enterprise connectors"], ["0", "", "lines of ETL required"]].map(([n, s, l], i) => (
          <motion.div key={l} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="neu rounded-3xl p-6">
            <div className="font-display text-6xl font-light text-[#0f2547] leading-none"><Counter to={Number(n)} suffix={s} /></div>
            <div className="text-sm text-slate-500 mt-2 font-body">{l}</div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const CAPS = [
  [Search, "Semantic schema inference", "Point at any raw log — the model auto-detects case IDs, activities, timestamps and resources. No mapping spreadsheets."],
  [GitBranch, "Auto process discovery", "Interactive process maps with performance overlays, variants and rework loops, generated in seconds."],
  [Cpu, "Cross-system correlation", "Stitch events across ERP, CRM and ITSM without brittle key-matching — the model reasons about causality."],
  [MessageSquare, "Ask in plain English", "A grounded AI analyst answers 'why is this slow?' with narrated, quantified root-cause analysis."],
  [Layers, "Unstructured fusion", "Turn emails, chats and tickets into structured process events that feed the very same map."],
  [Gauge, "Simulation & digital twin", "Model changes before you make them and see ROI-quantified impact on cycle time and throughput."],
];

function Capabilities() {
  return (
    <section id="capabilities" className="py-28 max-w-6xl mx-auto px-6">
      <div className="max-w-2xl mb-16">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0e7aa6] mb-3">One platform · every layer intelligent</div>
        <h2 className="font-display text-5xl font-semibold text-[#0f2547] leading-tight">LLM intelligence at every step of the stack</h2>
        <p className="text-slate-600 mt-5 text-lg font-body">From ingestion to insight, ProClytic applies semantic understanding where legacy tools rely on rigid rules and manual configuration.</p>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {CAPS.map(([Icon, t, d], i) => {
          const accent = i === 1; // one gradient accent tile
          return (
            <motion.div key={t} initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
              className={`${accent ? "pm-gradient text-white shadow-xl shadow-sky-900/20" : "neu"} rounded-3xl p-7 pm-transition pm-lift relative overflow-hidden`}>
              <div className="font-display text-6xl font-light opacity-10 absolute top-3 right-5">{String(i + 1).padStart(2, "0")}</div>
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-5 ${accent ? "bg-white/20" : "neu-inset"}`}><Icon className={`w-5 h-5 ${accent ? "text-white" : "text-[#0e7aa6]"}`} /></div>
              <h3 className={`font-semibold text-xl ${accent ? "text-white" : "text-[#0f2547]"} font-body`}>{t}</h3>
              <p className={`text-sm mt-2.5 leading-relaxed ${accent ? "text-white/85" : "text-slate-500"}`}>{d}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

const GAPS = [
  ["Weeks of ETL scripting & specialist consultants", "Zero-config, LLM-powered ingestion in minutes"],
  ["Rigid key-matching that breaks across systems", "Semantic correlation that reasons about causality"],
  ["Dashboards that show what — never why", "Narrated root-cause analysis in plain language"],
  ["Structured event logs only", "Fuses emails, chats & tickets into the same model"],
  ["Enterprise pricing locked to large teams", "Multi-tenant, mid-market accessible from day one"],
  ["Static reports", "Live digital twin, simulation & continuous monitoring"],
];

function Gaps() {
  return (
    <section id="gaps" className="py-28 relative overflow-hidden bg-[#0f2547]">
      <div className="absolute -right-24 top-6 w-80 h-80 rounded-full bg-[#0e7aa6]/30 blur-3xl" />
      <div className="absolute -left-24 bottom-0 w-80 h-80 rounded-full bg-[#16a9c7]/20 blur-3xl" />
      <div className="max-w-5xl mx-auto px-6 relative">
        <div className="max-w-2xl mb-14">
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#9db8d6] mb-3">The superset</div>
          <h2 className="font-display text-5xl font-semibold text-white leading-tight">Everything legacy process mining lacks — and more</h2>
          <p className="text-indigo-200/70 mt-5 text-lg font-body">We studied where traditional tools stop. ProClytic fills every gap and adds an AI layer on top.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {GAPS.map(([old, neu], i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="rounded-3xl p-5 bg-white/[0.06] border border-white/10 backdrop-blur-sm">
              <div className="text-sm text-indigo-200/50 line-through">{old}</div>
              <div className="text-base font-medium text-white mt-2 flex items-start gap-2 font-body"><Check className="w-4 h-4 text-[#16a9c7] mt-1 shrink-0" />{neu}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ROI() {
  const [cases, setCases] = useState([50000]);
  const [hours, setHours] = useState([6]);
  const [cost, setCost] = useState([45]);
  const c = cases[0], h = hours[0], rate = cost[0];
  const annualSavings = Math.round(c * h * rate * 0.28 * 0.35);
  const hoursSaved = Math.round(c * h * 0.28 * 0.35);
  return (
    <section id="roi" className="py-28 max-w-5xl mx-auto px-6">
      <div className="max-w-2xl mb-12">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0e7aa6] mb-3">Build the business case</div>
        <h2 className="font-display text-5xl font-semibold text-[#0f2547] leading-tight">Your ROI, calculated live</h2>
      </div>
      <div className="neu rounded-[32px] p-9 grid md:grid-cols-2 gap-12 items-center">
        <div className="space-y-8">
          {[["Annual process cases", cases, setCases, 5000, 500000, 5000, (v) => v.toLocaleString()],
            ["Avg hours per case", hours, setHours, 1, 40, 1, (v) => `${v}h`],
            ["Fully-loaded cost / hour", cost, setCost, 15, 150, 5, (v) => `$${v}`]].map(([l, val, set, min, max, step, fmt]) => (
            <div key={l}>
              <div className="flex justify-between text-sm mb-3 font-body"><span className="font-medium text-slate-600">{l}</span><span className="font-bold text-[#0e7aa6]">{fmt(val[0])}</span></div>
              <Slider value={val} onValueChange={set} min={min} max={max} step={step} data-testid={`roi-${l.slice(0, 4)}`} />
            </div>
          ))}
        </div>
        <motion.div key={annualSavings} initial={{ scale: 0.97, opacity: 0.7 }} animate={{ scale: 1, opacity: 1 }} className="rounded-[28px] pm-gradient p-9 text-white text-center shadow-xl shadow-sky-900/30">
          <div className="text-xs uppercase tracking-[0.2em] text-white/80">Estimated annual savings</div>
          <div className="font-display text-6xl font-semibold mt-3">${annualSavings.toLocaleString()}</div>
          <div className="mt-4 text-sm text-white/90 font-body">{hoursSaved.toLocaleString()} labour hours reclaimed / year</div>
        </motion.div>
      </div>
      <p className="text-xs text-slate-400 mt-4 font-body">Based on a typical 28% automatable steps and a 35% cycle-time reduction observed across deployments.</p>
    </section>
  );
}

function Platform() {
  const items = [
    [ShieldCheck, "Enterprise security", "AES-256, TLS 1.3, RBAC + ABAC, SSO, immutable audit and configurable data residency."],
    [Building2, "Multi-tenant by design", "Isolated tenants, super-admin onboarding and per-tenant model & key controls."],
    [Cpu, "Your choice of LLM", "Claude, GPT or Gemini — with bring-your-own-key for full data sovereignty."],
    [Database, "Connect anything", "Pre-built connectors, a generic REST/SQL SDK, file upload and unstructured fusion."],
  ];
  return (
    <section id="platform" className="py-24 max-w-6xl mx-auto px-6">
      <div className="grid lg:grid-cols-2 gap-5">
        {items.map(([Icon, t, d], i) => (
          <motion.div key={t} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className="neu rounded-3xl p-7 flex gap-5">
            <div className="w-12 h-12 rounded-2xl pm-gradient-dark flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-white" /></div>
            <div><h3 className="font-semibold text-lg text-[#0f2547] font-body">{t}</h3><p className="text-sm text-slate-500 mt-1.5">{d}</p></div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function CTA() {
  const navigate = useNavigate();
  return (
    <section className="pb-28 px-6">
      <div className="max-w-5xl mx-auto glass-strong rounded-[36px] p-14 text-center relative overflow-hidden">
        <div className="absolute -left-10 -bottom-10 w-64 h-64 rounded-full bg-[#16a9c7]/20 blur-3xl" />
        <div className="absolute -right-10 -top-10 w-64 h-64 rounded-full bg-[#0e7aa6]/20 blur-3xl" />
        <div className="relative">
          <div className="inline-flex w-14 h-14 rounded-full pm-gradient items-center justify-center mb-6 shadow-lg shadow-sky-900/30"><Zap className="w-6 h-6 text-white" /></div>
          <h2 className="font-display text-5xl font-semibold text-[#0f2547] leading-tight">Turn your event data into decisions today</h2>
          <p className="text-slate-600 mt-5 max-w-xl mx-auto text-lg font-body">Sign in to a fully-loaded demo workspace — real process mining, live AI analysis and a super-admin console, ready to explore.</p>
          <Button onClick={() => navigate("/login")} data-testid="cta-bottom" className={`${gradBtn} mt-9 h-12 px-9 text-base`}>Enter the platform<ArrowRight className="w-4 h-4 ml-1.5" /></Button>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-6 mt-16 pt-8 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-400 font-body">
        <div className="flex items-center gap-2"><div className="w-7 h-7 rounded-full pm-gradient flex items-center justify-center"><Brain className="w-4 h-4 text-white" /></div><span className="font-semibold text-slate-600">ProClytic</span></div>
        <span>© 2026 ProClytic · Process Intelligence, reimagined.</span>
      </div>
    </section>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen pm-app-bg">
      <Nav /><Hero /><Capabilities /><Gaps /><ROI /><Platform /><CTA />
    </div>
  );
}
